/** @jsx jsx */
/* @jsxFrag React.Fragment */

import { jsx, css } from '@emotion/core';
import React, { useMemo } from 'react';
import Translate from '../messages/Translate.js';

const ProgressBar = ({
  messages,
  formId,
  theme,
  fields,
  formType,
  activePageNumber,
  activeFieldIndex,
  totalPages,
  device,
  placement,
  mode,
  scrollbarWidth,
  userUiSettings,
  values,
  disableDefaultCSS = false
}) => {
  const translate = useMemo(() => new Translate(messages), [messages]);
  const fieldsCopy = fields.filter((field) => field.section === 'root' || !field.section);

  const uiSettings = useMemo(() => userUiSettings[formId] || {}, [formId, userUiSettings]);

  let activeIndex = activeFieldIndex;
  const totalFields = Object.keys(values).filter(
    (key) => (values[key].section === 'root' || !values[key].section) && values[key].visible && !values[key].hidden
  ).length;

  if (formType === 'conversational') {
    for (let field of fieldsCopy) {
      if (
        values[field._id] &&
        !values[field._id].visible &&
        fieldsCopy[activeFieldIndex] &&
        field.position < fieldsCopy[activeFieldIndex].position
      ) {
        activeIndex -= 1;
      }
    }

    if (fieldsCopy.length === activeFieldIndex) {
      activeIndex = totalFields;
    }
  }

  const width = formType === 'conversational' ? (activeIndex / totalFields) * 100 : (activePageNumber / totalPages) * 100;

  const styles = useMemo(
    () =>
      disableDefaultCSS
        ? {}
        : {
            main: css({
              width: '100%',
              height: `${theme.progressBarHeight}px`,
              position: 'fixed',
              top: 0,
              right: 0,
              zIndex: placement === 'builder' ? 4 : 1024,
              background: theme.progressBarBackgroundColor,
              boxSizing: 'border-box',
              '&': (() => {
                const obj = {};

                if (mode === 'preview') {
                  obj.right = 'auto';

                  if (placement === 'themes') {
                    obj.maxWidth = `calc(100% - 405px - ${scrollbarWidth}px)`;
                  }

                  if (placement === 'builder') {
                    obj.top = '82px';
                    obj.right = `${(uiSettings.builderRightBarWidth || 300) + scrollbarWidth}px`;

                    obj.maxWidth = `calc(100vw - ${
                      (uiSettings.builderLeftBarWidth || 300) + (uiSettings.builderRightBarWidth || 300) + 51 + scrollbarWidth
                    }px)`;
                  }

                  if (device === 'phone') {
                    obj.maxWidth = 400 - scrollbarWidth;
                  }

                  if (device === 'tablet') {
                    obj.maxWidth = 800 - scrollbarWidth;
                  }
                }

                return obj;
              })()
            }),
            bar: css({
              width: `${width}%`,
              transition: 'width 0.9s ease',
              height: `${theme.progressBarHeight}px`,
              background: theme.progressBarColor
            }),
            textParent: css({
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              position: 'fixed',
              zIndex: placement === 'builder' ? 4 : 1024,
              top: 0,
              right: 0,
              width: '100%',
              '&': (() => {
                const obj = {};

                if (mode === 'preview') {
                  obj.right = 'auto';

                  if (placement === 'themes') {
                    obj.maxWidth = `calc(100% - 405px - ${scrollbarWidth}px)`;
                  }

                  if (placement === 'builder') {
                    obj.top = '82px';
                    obj.right = `${(uiSettings.builderRightBarWidth || 300) + scrollbarWidth + 10}px`;

                    obj.maxWidth = `calc(100vw - ${
                      (uiSettings.builderLeftBarWidth || 300) + (uiSettings.builderRightBarWidth || 300) + 51 + scrollbarWidth
                    }px)`;
                  }

                  if (device === 'phone') {
                    obj.maxWidth = 400 - scrollbarWidth;
                  }

                  if (device === 'tablet') {
                    obj.maxWidth = 800 - scrollbarWidth;
                  }
                }

                return obj;
              })()
            }),
            text: css({
              padding: `${theme.progressBarBadgeHorizontalPadding}px ${theme.progressBarBadgeVerticalPadding}px`,
              position: 'fixed',
              fontFamily: 'Roboto',
              lineHeight: '142%',
              fontWeight: theme.progressBarBadgeWeight,
              fontSize: `${theme.progressBarBadgeFontSize}px`,
              top: `${theme.progressBarHeight + 5}px`,
              right: '5px',
              zIndex: placement === 'builder' ? 4 : 1024,
              background: theme.progressBarBadgeBackgroundColor,
              color: theme.progressBarBadgeColor,
              borderRadius: `${theme.progressBarBadgeRoundness}px`,
              border: `${theme.progressBarBadgeBorderSize}px solid ${theme.progressBarBadgeBorderColor}`,
              '&': (() => {
                const obj = {};

                if (mode === 'preview') {
                  obj.position = 'relative';
                }

                return obj;
              })()
            })
          },
    [
      device,
      mode,
      placement,
      scrollbarWidth,
      theme.progressBarBackgroundColor,
      theme.progressBarBadgeBackgroundColor,
      theme.progressBarBadgeBorderColor,
      theme.progressBarBadgeBorderSize,
      theme.progressBarBadgeColor,
      theme.progressBarBadgeFontSize,
      theme.progressBarBadgeHorizontalPadding,
      theme.progressBarBadgeRoundness,
      theme.progressBarBadgeVerticalPadding,
      theme.progressBarBadgeWeight,
      theme.progressBarColor,
      theme.progressBarHeight,
      uiSettings.builderLeftBarWidth,
      uiSettings.builderRightBarWidth,
      width
    ]
  );

  if (formType === 'classic' && totalPages === 1) return <></>;

  const TextParent = ({ children }) => {
    if (mode === 'preview') return <div css={styles.textParent}>{children}</div>;

    return <>{children}</>;
  };

  return (
    <>
      <div css={styles.main}>
        <div css={styles.bar} />
      </div>

      <TextParent>
        <div css={styles.text}>
          {formType === 'conversational' &&
            translate.text('progressBarConversational', {
              currentField: { value: activeIndex || '0' },
              totalFields: { value: totalFields }
            })}
          {formType === 'classic' &&
            translate.text('progressBarTraditional', {
              currentPage: { value: activePageNumber },
              totalPages: { value: totalPages }
            })}
        </div>
      </TextParent>
    </>
  );
};

export default ProgressBar;
